import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  createContractAIConfig,
  deleteContractAIConfig,
  getContractAIConfigs,
  setDefaultContractAIConfig,
  updateContractAIConfig,
} from "../../api";

const { Text } = Typography;
const { TextArea } = Input;

export default function ContractAIConfig() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [defaults, setDefaults] = useState({ prompt: "", max_tokens: 4096, temperature: 0, thinking_enabled: false });
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getContractAIConfigs();
      setItems(Array.isArray(res?.data) ? res.data : []);
      if (res?.defaults) setDefaults(res.defaults);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      is_default: items.length === 0,
      timeout_seconds: 60,
      prompt: defaults.prompt,
      max_tokens: defaults.max_tokens || 4096,
      temperature: defaults.temperature ?? 0,
      thinking_enabled: defaults.thinking_enabled ?? false,
    });
    setOpen(true);
  };

  const showEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      api_url: record.api_url,
      api_key: "",
      model: record.model,
      prompt: record.prompt || defaults.prompt,
      max_tokens: record.max_tokens || defaults.max_tokens || 4096,
      temperature: record.temperature ?? defaults.temperature ?? 0,
      thinking_enabled: record.thinking_enabled ?? false,
      enabled: record.enabled,
      is_default: record.is_default,
      timeout_seconds: record.timeout_seconds || 60,
    });
    setOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await updateContractAIConfig(editing.id, values);
      } else {
        await createContractAIConfig(values);
      }
      message.success(editing ? "AI 配置已更新" : "AI 配置已添加");
      setOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const choose = async (record) => {
    await setDefaultContractAIConfig(record.id);
    message.success(`入住合同将调用：${record.name}`);
    await load();
  };

  const remove = async (record) => {
    await deleteContractAIConfig(record.id);
    message.success("AI 配置已删除");
    await load();
  };

  const columns = [
    { title: "配置名称", dataIndex: "name", key: "name" },
    { title: "模型", dataIndex: "model", key: "model" },
    { title: "Max Tokens", dataIndex: "max_tokens", key: "max_tokens", width: 110 },
    {
      title: "深度思考",
      dataIndex: "thinking_enabled",
      key: "thinking_enabled",
      width: 100,
      render: (enabled) => <Tag color={enabled ? "purple" : "default"}>{enabled ? "开启" : "关闭"}</Tag>,
    },
    {
      title: "API 地址",
      dataIndex: "api_url",
      key: "api_url",
      ellipsis: true,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    { title: "密钥", dataIndex: "api_key_masked", key: "api_key_masked", width: 130 },
    {
      title: "状态",
      key: "status",
      width: 170,
      render: (_, record) => (
        <Space>
          <Tag color={record.enabled ? "green" : "default"}>{record.enabled ? "启用" : "停用"}</Tag>
          {record.is_default && <Tag color="blue">当前调用</Tag>}
        </Space>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => showEdit(record)}>编辑</Button>
          <Button size="small" type={record.is_default ? "default" : "primary"} disabled={record.is_default || !record.enabled} onClick={() => choose(record)}>
            {record.is_default ? "使用中" : "选择调用"}
          </Button>
          <Popconfirm title="确认删除这项 AI 配置？" description={record.is_default ? "删除后合同识别将暂停，直到选择其他配置。" : "删除后不可恢复。"} onConfirm={() => remove(record)}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2>合同 AI 识别配置</h2>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="小程序只调用标记为“当前调用”的启用配置"
        description="接口需兼容 OpenAI Chat Completions 多模态请求。API Key 仅保存在后端，列表不会返回明文。"
      />
      <Card
        title="AI 接口列表"
        extra={<Button type="primary" onClick={showCreate}>添加配置</Button>}
      >
        <Table rowKey="id" loading={loading} dataSource={items} columns={columns} pagination={false} />
      </Card>

      <Modal
        title={editing ? "编辑 AI 配置" : "添加 AI 配置"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={save}
        confirmLoading={saving}
        width={820}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="配置名称" rules={[{ required: true, message: "请输入配置名称" }]}>
            <Input placeholder="例如：合同识别主接口" maxLength={100} />
          </Form.Item>
          <Form.Item name="api_url" label="API 地址" rules={[{ required: true, type: "url", message: "请输入有效的 API 地址" }]} extra="可填写到 /v1，系统会自动追加 /chat/completions；也可填写完整地址。">
            <Input placeholder="https://api.example.com/v1" />
          </Form.Item>
          <Form.Item
            name="api_key"
            label="API Key"
            rules={editing ? [] : [{ required: true, message: "请输入 API Key" }]}
            extra={editing ? `留空保留原密钥（当前：${editing.api_key_masked || "未设置"}）` : "密钥不会返回到浏览器列表中"}
          >
            <Input.Password placeholder={editing ? "留空表示不修改" : "请输入 API Key"} autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="model" label="视觉模型名称" rules={[{ required: true, message: "请输入模型名称" }]}>
            <Input placeholder="填写供应商提供的视觉模型 ID" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="prompt"
            label="合同识别提示词"
            rules={[{ required: true, whitespace: true, message: "请输入合同识别提示词" }]}
            extra="提示词只要求模型返回 property_name、room_number、tenant_phone、monthly_rent、deposit_amount、bill_generate_day、rent_start_day、contract_start_date、contract_end_date、other_fees；不需要合同摘要和警示。"
          >
            <TextArea rows={12} showCount maxLength={12000} placeholder="请输入系统提示词" />
          </Form.Item>
          <Space style={{ marginBottom: 16 }}>
            <Button onClick={() => form.setFieldValue("prompt", defaults.prompt)}>恢复系统默认提示词</Button>
          </Space>
          <Form.Item name="max_tokens" label="max_tokens" rules={[{ required: true, message: "请输入最大输出 Token 数" }]} extra="建议 2048～4096；数值过大可能增加延迟和费用。">
            <InputNumber min={256} max={16384} step={256} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="temperature" label="temperature" rules={[{ required: true, message: "请输入温度参数" }]} extra="合同提取建议使用 0；越高越随机。">
            <InputNumber min={0} max={2} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="thinking_enabled"
            label="深度思考"
            valuePropName="checked"
            extra="合同结构化提取建议关闭，可减少等待时间和推理内容干扰。DeepSeek 接口会显式发送 thinking.enabled/disabled；其他供应商保持原协议不变。"
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item name="timeout_seconds" label="超时时间（秒）" rules={[{ required: true }]}>
            <InputNumber min={10} max={120} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_default" label="保存后设为当前调用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
